import type { RatingT } from '@mentimeter/question-modules-types';
import type { RatingStaticContent } from '@core-api/presentation/types/response';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { RatingGridPresentation } from './RatingGridPresentation';

export function RatingGridPresentationDataHandler() {
  const {
    useQuestion,
    InteractiveCanvasComponents,
    useConfig,
    useResults,
    usePace,
  } = usePresentationContext();
  const { enableAnimations, mode } = useConfig();

  const { currentStep } = usePace();

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const results = useResults<RatingT>();

  const slide = useQuestion();

  const staticContent = slide.staticContent as RatingStaticContent;

  const range = {
    min: slide.interactiveContents[0]!.responseRange?.min ?? 0,
    max: slide.interactiveContents[0]!.responseRange?.max ?? 10,
  };

  const is4Values =
    staticContent.interactiveContentStyling.visualization === 'four-values';

  const { xAxisLabels, yAxisLabels } = is4Values
    ? getHacky4x4LegacyAxisLabels({
        yAxisLabels: staticContent.interactiveContentStyling.yAxisLabels,
        xAxisLabels: staticContent.interactiveContentStyling.xAxisLabels,
      })
    : {
        xAxisLabels: staticContent.interactiveContentStyling.xAxisLabels,
        yAxisLabels: staticContent.interactiveContentStyling.yAxisLabels,
      };

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    visualizationState === 'hide-answers';

  return (
    <RatingGridPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={slide.interactiveContents[0]!.description}
      fontSizeOffset={staticContent.textSizeOffset}
      shouldHideResults={shouldHideResults}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
      choices={slide.interactiveContents[0]!.choices.map((choice, index) => ({
        id: choice.legacyChoiceId ?? index,
        label: choice.title,
      }))}
      shouldAnimate={enableAnimations}
      results={results}
      xAxisLabels={xAxisLabels}
      yAxisLabels={yAxisLabels}
      range={range}
      backgroundImage={
        slide.interactiveContents[0]!.image
          ? { url: slide.interactiveContents[0]!.image.presets.original.url }
          : null
      }
    />
  );
}

/**
 * Why did we do this?
 * We deleted the four-values visualization type without migrating existing slides.
 * Which means we lost the ability to display min/max labels, but the old slides still has these values set.
 * And Impact/Effort is set on all slides for x and y axis labels by default.
 * In order to display the old slides correctly, we need to override the axis labels and use the max values as the axis labels.
 * This is a hacky way to do it, but best Impact/Effort solution ATM.
 */
const getHacky4x4LegacyAxisLabels = ({
  xAxisLabels,
  yAxisLabels,
}: {
  xAxisLabels: {
    axisLabel: string;
    minimumValueLabel: string;
    maximumValueLabel: string;
  };
  yAxisLabels: {
    axisLabel: string;
    minimumValueLabel: string;
    maximumValueLabel: string;
  };
}) => {
  let overrideXAxisLabel = xAxisLabels.axisLabel;
  let overrideYAxisLabel = yAxisLabels.axisLabel;

  if (
    xAxisLabels.axisLabel === 'Effort' &&
    !!xAxisLabels.maximumValueLabel &&
    yAxisLabels.axisLabel === 'Impact' &&
    !!yAxisLabels.maximumValueLabel
  ) {
    overrideXAxisLabel = xAxisLabels.maximumValueLabel;
    overrideYAxisLabel = yAxisLabels.maximumValueLabel;
  }

  return {
    xAxisLabels: {
      axisLabel: overrideXAxisLabel,
      minimumValueLabel: xAxisLabels.minimumValueLabel,
      maximumValueLabel: xAxisLabels.maximumValueLabel,
    },
    yAxisLabels: {
      axisLabel: overrideYAxisLabel,
      minimumValueLabel: yAxisLabels.minimumValueLabel,
      maximumValueLabel: yAxisLabels.maximumValueLabel,
    },
  };
};
